export type MemberTier =
  | 'Gold Life Member'
  | 'Silver Member'
  | 'Corporate Member'
  | 'Patron Member'
  | 'Associate Member'

export type MemberStatus = 'Active' | 'Pending' | 'Suspended'

export interface MemberTransaction {
  id: string
  title: string
  date: string
  amount: number
  status: 'Paid' | 'Pending'
}

export interface Member {
  id: string
  name: string
  email: string
  phone: string
  tier: MemberTier
  status: MemberStatus
  joinedDate: string
  joinedTimestamp: number
  outstandingBalance: number
  lastVisit: string
}

const FIRST_NAMES = [
  'Arjun',
  'Sai',
  'Venkata',
  'Lakshmi',
  'Padma',
  'Ravi',
  'Srinivas',
  'Anitha',
  'Naveen',
  'Divya',
  'Kiran',
  'Suresh',
  'Madhavi',
  'Prasad',
  'Swathi',
  'Ramesh',
  'Deepak',
  'Sridevi',
  'Mohan',
  'Vani',
  'Krishna',
  'Aravind',
  'Sunitha',
  'Bhargav',
  'Harika',
  'Teja',
  'Nikhil',
  'Sravani',
  'Chaitanya',
  'Manasa',
  'Rajesh',
  'Yamini',
]

const LAST_NAMES = [
  'Reddy',
  'Naidu',
  'Rao',
  'Sharma',
  'Chowdary',
  'Varma',
  'Prasad',
  'Gupta',
  'Raju',
  'Kumar',
  'Devi',
  'Murthy',
  'Sastry',
  'Pillai',
  'Mohan',
  'Choudhary',
]

const TIERS: MemberTier[] = [
  'Gold Life Member',
  'Silver Member',
  'Corporate Member',
  'Patron Member',
  'Associate Member',
]

// Deterministic pseudo-random based on an integer seed (stable across renders).
function seeded(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 233280
  return x - Math.floor(x)
}

const NOW = new Date('2026-06-28T00:00:00')
const MS_PER_DAY = 86400000

function generateMembers(): Member[] {
  const list: Member[] = []
  for (let i = 0; i < 3000; i++) {
    const first = FIRST_NAMES[Math.floor(seeded(i + 1) * FIRST_NAMES.length)]
    const last = LAST_NAMES[Math.floor(seeded(i + 7) * LAST_NAMES.length)]
    const name = `${first} ${last}`
    const id = `CH-${1001 + i}`

    const tier = TIERS[Math.floor(seeded(i + 13) * TIERS.length)]

    // ~85% Active, ~9% Pending, ~6% Suspended
    const statusRoll = seeded(i + 19)
    const status: MemberStatus =
      statusRoll > 0.94 ? 'Suspended' : statusRoll > 0.85 ? 'Pending' : 'Active'

    // Joined within the last ~9 years (since 2017); some within the last 30 days.
    const daysAgo = Math.floor(seeded(i + 23) * 3300)
    const joined = new Date(NOW.getTime() - daysAgo * MS_PER_DAY)
    const joinedDate = joined.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })

    // ~30% carry an outstanding balance
    const hasDues = seeded(i + 29) > 0.7
    const outstandingBalance = hasDues
      ? Math.round((500 + seeded(i + 31) * 9500) / 50) * 50
      : 0

    const visitDaysAgo = Math.floor(seeded(i + 37) * 90)
    const lastVisit = new Date(
      NOW.getTime() - visitDaysAgo * MS_PER_DAY,
    ).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })

    const phone =
      `+91 ${9000000000 + Math.floor(seeded(i + 41) * 999999999)}`.slice(0, 14)

    const emailHandle = `${first}.${last}`.toLowerCase()
    const email = `${emailHandle}${i}@example.com`

    list.push({
      id,
      name,
      email,
      phone,
      tier,
      status,
      joinedDate,
      joinedTimestamp: joined.getTime(),
      outstandingBalance,
      lastVisit,
    })
  }
  return list
}

export const members: Member[] = generateMembers()

export function getMemberById(id: string | undefined): Member | undefined {
  if (!id) return undefined
  return members.find((m) => m.id === id)
}

// Deterministic mock transaction history for a member detail view.
const TX_TITLES = [
  'Monthly Subscription',
  'Restaurant Tab',
  'Tennis Court Booking',
  'Banquet Hall Deposit',
  'Spa & Wellness',
  'Guest Pass',
  'Bar Tab',
  'Annual Membership Fee',
]

export function getMemberTransactions(member: Member): MemberTransaction[] {
  const seedBase = parseInt(member.id.replace('CH-', ''), 10)
  const count = 4 + Math.floor(seeded(seedBase + 3) * 4)
  const txns: MemberTransaction[] = []
  for (let j = 0; j < count; j++) {
    const title =
      TX_TITLES[Math.floor(seeded(seedBase + j + 51) * TX_TITLES.length)]
    const amount =
      Math.round((300 + seeded(seedBase + j + 53) * 4700) / 50) * 50
    const daysAgo = Math.floor(seeded(seedBase + j + 59) * 120)
    const date = new Date(
      NOW.getTime() - daysAgo * MS_PER_DAY,
    ).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    // If the member has dues, the most recent transaction reads as Pending.
    const status: 'Paid' | 'Pending' =
      member.outstandingBalance > 0 && j === 0 ? 'Pending' : 'Paid'
    txns.push({ id: `${member.id}-tx-${j}`, title, date, amount, status })
  }
  return txns
}

export interface MemberStats {
  total: number
  activeCount: number
  pendingCount: number
  suspendedCount: number
  totalOutstanding: number
  newThisMonth: number
  tierBreakdown: { tier: MemberTier; count: number }[]
}

export function getMemberStats(): MemberStats {
  const thirtyDaysAgo = NOW.getTime() - 30 * MS_PER_DAY
  let activeCount = 0
  let pendingCount = 0
  let suspendedCount = 0
  let totalOutstanding = 0
  let newThisMonth = 0
  const tierCounts: Record<string, number> = {}

  for (const m of members) {
    if (m.status === 'Active') activeCount++
    else if (m.status === 'Pending') pendingCount++
    else if (m.status === 'Suspended') suspendedCount++
    totalOutstanding += m.outstandingBalance
    if (m.joinedTimestamp >= thirtyDaysAgo) newThisMonth++
    tierCounts[m.tier] = (tierCounts[m.tier] || 0) + 1
  }

  const tierBreakdown = TIERS.map((tier) => ({
    tier,
    count: tierCounts[tier] || 0,
  }))

  return {
    total: members.length,
    activeCount,
    pendingCount,
    suspendedCount,
    totalOutstanding,
    newThisMonth,
    tierBreakdown,
  }
}

export const ALL_TIERS = TIERS
export const ALL_STATUSES: MemberStatus[] = ['Active', 'Pending', 'Suspended']

export function formatINR(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`
}
